/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useRef } from 'react';

import { nanoid } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useModal, useTapped } from '@utils/hooks';
import { PaymentSystemsEnum } from '@utils/types';

import { GETCardsResponse } from '@modules/cards/types';

import AnyCard from './any.svg';
import DotsIcon from './dots.svg';
import MasterCardIcon from './mastercard.svg';
import RupayCardIcon from './rupaycard.svg';
import UnionPayIcon from './union.svg';
import VisaIcon from './visa.svg';

import style from './Card.scss';

const { visa, mastercard, mastercarddebit, union_pay, rupay } = PaymentSystemsEnum;

const cn = bemCssModules(style, 'Card');
const ti = getTestId('Card');

const renderIcon = (ps: GETCardsResponse['payment_system']) => {
  const paymentSystem = (ps || '').toLocaleLowerCase();

  return (
    {
      [visa]: <VisaIcon />,
      [mastercard]: <MasterCardIcon />,
      [mastercarddebit]: <MasterCardIcon />,
      [union_pay]: <UnionPayIcon />,
      [rupay]: <RupayCardIcon />,
    }[paymentSystem] || <AnyCard />
  );
};

export type TCard = {
  id: string;
  paymentSystem: GETCardsResponse['payment_system'];
  cardNumber: string;
  bank?: {
    title: string;
    color: {
      background: string;
      foreground: string;
    };
    icon: string;
  };
  onDelete(id: string): void;
  testId?: string;
};

const Card: CustomFC<TCard> = props => {
  const { id, paymentSystem, cardNumber, onDelete, className, testId } = props;

  const { t } = useTranslation();

  const { handlers, isTapped } = useTapped();

  const ref = useRef<HTMLButtonElement>(null);

  const [menuIsOpen, setMenuIsOpen] = useModal([ref]);

  const formattedCardNumber = cardNumber.substring(cardNumber.length - 4);
  const cardName = paymentSystem || t('cards.unknown');
  const paymentSystemName = cardName === mastercarddebit ? mastercard : cardName;
  const cardText = `${paymentSystemName} · ${formattedCardNumber}`;
  const removeCardText = `${t('cards.remove_card')} · ${formattedCardNumber}`;

  const handleDeleteCard = () => {
    onDelete(id);
    setMenuIsOpen(false);
  };

  const menuList = [
    {
      text: removeCardText,
      onClick: () => handleDeleteCard(),
      dataTestId: ti('remove-button'),
    },
  ];

  return (
    <button
      className={cn({ isTapped }, [className])}
      type="button"
      ref={ref}
      data-testid={ti(testId)}
      {...handlers}
    >
      <span className={cn('icon')}>{renderIcon(paymentSystem)}</span>
      <span className={cn('text')}>{cardText}</span>
      <DotsIcon
        className={cn('dots')}
        data-testid={ti('menu-button')}
        onClick={() => setMenuIsOpen(!menuIsOpen)}
      />
      {menuList.map(({ text, onClick, dataTestId }) => (
        <div
          key={nanoid()}
          className={cn('menu', { isOpen: menuIsOpen })}
          onClick={onClick}
          data-testid={dataTestId}
        >
          {text}
        </div>
      ))}
    </button>
  );
};

export default Card;
